import { AnimatePresence, motion } from "motion/react";
import { string } from "prop-types";
import { Suspense, lazy, memo } from "react";

import styled from "styled-components/macro";

import FnrButtonWrapper from "UI/FnrButtonWrapper";
import FnrIcon from "UI/FnrIcon";
import { marketingColors, navAccentColors, navColors } from "UI/colors";

import Portal from "Utilities/PortalMaker";

import configsByPeekHandleKey from "./configsByPeekHandleKey";
import { usePeekHandle } from "./peekHandleHooks";
import { RE_SETUP } from "./peekHandleKeys";

const Z_PEEK_HANDLE = 6700;

const PeekHandleContents = memo(({ peekHandleKey }) => {
  let Component;
  switch (peekHandleKey) {
    case RE_SETUP:
      Component = lazy(() =>
        import("RESetup/components/PeekHandle/PeekHandleContent")
      );
      break;
    default:
      Component = () => null;
  }
  return (
    <Suspense fallback={null}>
      <Component />
    </Suspense>
  );
});

PeekHandleContents.propTypes = {
  peekHandleKey: string,
};

export default function PeekHandle() {
  const [{ isActive, peekHandleKey }, { expandPeekHandle }] = usePeekHandle();

  const portalTarget = document.getElementById("FnrOverlayHolder");
  if (!portalTarget) return null;

  const navColorKey = configsByPeekHandleKey.navColorKey[peekHandleKey];

  const accentColor = navColorKey
    ? navColors[navColorKey]
    : marketingColors.fnBlue;
  const [, activeColor] = navColorKey
    ? navAccentColors[navColorKey]
    : [marketingColors.blue, marketingColors.darkBlue];

  return (
    <Portal to={portalTarget}>
      <AnimatePresence>
        {isActive ? (
          <HandleSlider
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{
              type: "tween",
              duration: 0.45,
              ease: [0.4, 0, 0.2, 1],
            }}
          >
            <Handle
              onClick={expandPeekHandle}
              $accentColor={accentColor}
              $activeColor={activeColor}
              aria-label="Open RE Setup"
              data-cy="peek-handle"
            >
              <PeekHandleContents peekHandleKey={peekHandleKey} />
            </Handle>
          </HandleSlider>
        ) : null}
      </AnimatePresence>
    </Portal>
  );
}

const HandleSlider = styled(motion.div)`
  position: fixed;
  right: 0;
  top: 0;
  bottom: 0;
  z-index: ${Z_PEEK_HANDLE};
  display: flex;
  align-items: center;
`;

const Handle = styled(FnrButtonWrapper)`
  --peek-accent: ${({ $accentColor }) => $accentColor};
  --peek-active: ${({ $activeColor }) => $activeColor};
  width: 36px;
  max-height: calc(100vh - 32px);
  overflow: hidden;
  background: #fff;
  border-radius: 32px 0 0 32px;
  box-shadow: -2px 0 8px rgba(0, 0, 0, 0.15);
  padding: 24px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  transition: background 0.2s;

  &:hover,
  &:focus-visible {
    background: color-mix(in srgb, var(--peek-accent) 6%, white);
  }

  &:active {
    background: color-mix(in srgb, var(--peek-accent) 12%, white);
  }
`;

const Label = styled.span`
  writing-mode: vertical-lr;
  transform: rotate(180deg);
  font-size: 14px;
  font-weight: 600;
  color: #9ca3af;
  letter-spacing: 0.02em;
  white-space: nowrap;
  max-height: 260px;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: color 0.2s;

  ${Handle}:hover & {
    color: var(--peek-accent);
  }

  ${Handle}:active & {
    color: var(--peek-active);
  }
`;

const ChevronIcon = styled(FnrIcon)`
  font-size: 11px;
  color: #9ca3af;
  flex-shrink: 0;
  transition: color 0.2s;

  ${Handle}:hover & {
    color: var(--peek-accent);
  }

  ${Handle}:active & {
    color: var(--peek-active);
  }
`;

export { ChevronIcon, Label };
